import { Component, EventEmitter, Input, Output } from '@angular/core';
import {
  faChevronDown,
  faChevronUp,
  faTrashAlt,
} from '@fortawesome/free-solid-svg-icons';
import { Observable, ReplaySubject, Subject, concat, throwError } from 'rxjs';
import { catchError, first, takeUntil, tap, toArray } from 'rxjs/operators';
import { FileDetails } from 'src/app/product-capture/import-product/import-product.consts';
import { DocumentUtils } from 'src/app/product-capture/product-capture-details/overview/documents/document.utils';
import { GetErrorMessageFromResponse } from 'src/app/product-capture/utils/get-error-message-from-response';

@Component({
  selector: 'app-dc-file-upload',
  templateUrl: './dc-file-upload.component.html',
  styleUrls: ['./dc-file-upload.component.scss'],
})
export class DcFileUploadComponent {
  @Input()
  acceptedFiles: string;

  @Input()
  allowMultipleFiles = false;

  @Input()
  autoLoad = true;

  @Input()
  maxFileSize: number;

  icons = {
    up: faChevronUp,
    down: faChevronDown,
  };

  @Output()
  fileLoaded: EventEmitter<FileDetails> = new EventEmitter();

  @Output()
  filesChanged = new EventEmitter<FileDetails[]>();

  fileData: File = null;
  uploadDate: string = '';

  uploadInProgress = false;

  uploadFinished = false;

  progressValue = 0;

  currentFile: FileDetails;

  files: FileDetails[] = [];

  totalSize: number = 0;

  destroy$ = new Subject<boolean>();

  removeIcon = faTrashAlt;

  errors = [];

  constructor() {}

  formatErrorMessage(error: any): string {
    return GetErrorMessageFromResponse(error);
  }

  onInputFileSelected(event: Event): void {
    const files = (event.target as HTMLInputElement).files;
    if (this.allowMultipleFiles) {
      Array.from(files).forEach((file: File) => {
        const fileDetails: FileDetails = {
          file,
        };

        this.queueFile(fileDetails);
      });
    } else {
      this.queueFile({ file: files[0] });
    }

    this.filesChanged.emit(this.files);
    this.autoLoad && this.startQueue().subscribe();
  }

  queueFile(fileDetails: FileDetails) {
    if (!fileDetails) {
      return;
    }

    const uploader = this;
    const fileProxy = new Proxy(fileDetails, {
      get(target, prop) {
        return target[prop];
      },

      set(target, prop, value) {
        target[prop] = value;

        if (prop === 'error') {
          uploader.errors = [...uploader.errors, target];
        }

        if (prop === 'progress' && value === target.file.size) {
          uploader.uploadInProgress = !!uploader.files.find(
            (file) => file.progress !== file.file.size
          );
        }

        return true;
      },
    });

    const { file } = fileProxy;
    this.totalSize += file.size;
    fileProxy.progress = 0;

    this.files.push(fileProxy);
  }

  formatFileSize(size: number): string {
    return DocumentUtils.formatSize(size);
  }

  startQueue(): Observable<FileDetails[]> {
    this.uploadFinished = false;
    this.uploadInProgress = true;

    const observables = this.files.reduce(
      (acc: Observable<FileDetails>[], current: FileDetails, index: number) => {
        if (current.processed) {
          return acc;
        }

        acc.push(
          this.createObservableForFile(current, index ? acc[index] : null)
        );
        return acc;
      },
      []
    );

    return concat(...observables).pipe(
      takeUntil(this.destroy$),
      toArray(),
      tap((results: FileDetails[]) => {
        this.uploadFinished = false;
        this.uploadInProgress = true;
      }),
      catchError((error) => {
        this.uploadInProgress = false;
        this.uploadFinished = true;
        return throwError(() => error);
      })
    );
  }

  createObservableForFile(
    details: FileDetails,
    prevObservable: Observable<FileDetails>
  ): Observable<FileDetails> {
    const obs = new ReplaySubject<FileDetails>();

    this.currentFile = details;
    const { file: workingFile } = details;

    const reader = new FileReader();
    this.destroy$.pipe(first()).subscribe(() => {
      reader.abort();
    });

    reader.onprogress = (event: ProgressEvent<EventTarget>) => {
      this.progressValue += event.loaded - details.progress;
      details.progress = event.loaded / 2;
    };

    reader.onload = () => {
      !this.allowMultipleFiles && this.fileLoaded.emit(details);
      details.content = reader.result;
      obs.complete();
    };

    if (this.acceptedFiles?.length) {
      if (!this.acceptedFiles.includes(workingFile.name.split('.').pop())) {
        details.error = `Invalid file types. The only accepted file types are ${this.acceptedFiles}. This file wasn't uploaded.`;
      }
    }

    if (workingFile.size > this.maxFileSize && !details.error) {
      details.error = `File size (${DocumentUtils.formatSize(
        workingFile.size
      )}) exceeds the maximum allowed of ${DocumentUtils.formatSize(
        this.maxFileSize
      )}.`;
    }

    if (details.error) {
      obs.next(null);
      obs.complete();
    } else {
      !prevObservable && obs.next(details);
    }

    if (prevObservable) {
      prevObservable.pipe(first()).subscribe((result) => obs.next(result));
    }

    return obs.asObservable().pipe(
      takeUntil(this.destroy$),
      tap(() => !details.error && reader.readAsText(workingFile, 'UTF-8'))
    );
  }

  onFilesChanged(files: FileDetails[]): void {
    files.forEach((details) => this.queueFile(details));
    this.autoLoad && this.startQueue().subscribe();
  }

  removeFile(removedFile: FileDetails, input: HTMLInputElement) {
    this.files = this.files.filter((file) => file.file !== removedFile.file);
    this.errors = this.errors.filter((file) => file.file !== removedFile.file);

    input.value = this.files as any;
    this.progressValue -= removedFile.file.size;
    this.totalSize -= removedFile.file.size;

    if (!this.allowMultipleFiles) {
      this.fileLoaded.emit(this.files?.[0]);
    }

    this.filesChanged.emit(this.files);
  }

  isObject(obj: any): boolean {
    return obj instanceof Object;
  }

  cancel(): void {
    this.destroy$.next(true);
  }
}
